import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import { CONSTANTS, MEDIA_TYPE_EXTENSIONS, UPLOAD_FILE_LOCATION_TYPE } from "@constants";
import {
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mzc-pdc/ui";
import TooltipButton from "../../components_v2/tooltip-button/tooltip-button";
import {
    IconArrowDownload,
    IconArrowUpload,
    IconClose,
    IconMoreHoriz,
    IconResetSmall,
    IconSeparator,
    IconStorage,
    ImageBucketS3Off,
    ImageBucketS3On,
    ImageEmpty,
    ImageFolderSmallOn,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import StorageSyncTooltip from "@components/tooltip/StorageSyncTooltip";
import Menu from "@features/menu/menu";
import { nanoid } from "nanoid";
import { clear } from "@modules/uploadQueue";
import { getStageSettings } from "@modules/settings";
import { useHistory, useRouteMatch } from "react-router";
import ObjectIcon from "@routes/sources/components/ObjectIcon";
import convertToByte from "@cores/convertToByte";
import { listFolders, listObjectsV3, listResourceBucketsV2 } from "@modules/apis/storages";
import fp from "lodash/fp";
import { axios } from "@cores/axiosWrapper";
import getFilesInS3 from "@cores/getFilesInS3";
import { alpha } from "@mui/material";
import EllipsisTooltip, { EllipsisTypography } from "@components_v2/ellipsis-tooltip";

const REGEXP_SCHEME = new RegExp("^(s3|https)://", "i");
let pollingTimeout;

const DialogSelectS3FilesV2 = ({ open, accept, onClose, onSubmit, isMultiFileSelect = true }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const match = useRouteMatch();
    const history = useHistory();

    const stickyDomRef = useRef();

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const createAsset = useSelector(({ createAsset }) => createAsset);

    const hierarchyActive = "folder";
    const [selectedBucket, setSelectedBucket] = useState(null);
    const [selectPending, setSelectPending] = useState(false);
    const [checkedFiles, setCheckedFiles] = useState([]);

    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0.1,
        rootMargin: "20px",
    });

    const [sources, setSources] = useState({
        hierarchy: { pending: false, isActiveView: "folder", folder: [] },
        objects: [],
        search: {
            isFolderSearch: true,
            keyword: "",
            storageClass: "",
            beginDate: null,
            endDate: null,
            offset: 0,
            limit: 200,
            defaultViewCountByToken: 200,
        },
    });

    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({
        totalCount: 0,
        offset: 0,
        data: [],
    });
    const [objectsCancelTokenSource, setObjectsCancelTokenSource] = useState();

    const [state, setState] = useState({
        pending: true,
        bucketPath: null,
        selectedItems: [],
        isChecked: false,
    });

    const hierarchy = useMemo(() => sources.hierarchy, [sources]);
    const folderList = useMemo(() => ((sources && sources.objects) || []).filter((o) => o.isFolder), [sources]);
    const fileList = useMemo(() => ((sources && sources.objects) || []).filter((o) => !o.isFolder), [sources]);

    const clearPolling = () => {
        if (pollingTimeout) {
            clearTimeout(pollingTimeout);
        }
    };

    const getFileUrl = (file) => {
        const folderPath = state.bucketPath.folderPath ? `${state.bucketPath.folderPath}/` : "";
        const url = `s3://${state.bucketPath.bucket}/${folderPath}${file.path}`;
        return url;
    };

    const getAcceptedExtensions = useCallback(() => {
        if (!accept) return null;

        if (accept.type === "mediaType") {
            return accept.value.reduce((acc, cur) => {
                return acc.concat(MEDIA_TYPE_EXTENSIONS[cur]);
            }, []);
        } else if (accept.type === "extension") {
            return accept.value;
        }
        return null;
    }, [accept]);

    const validateObject = (file) => {
        if (!file) {
            return null;
        }

        const acceptedExtensions = getAcceptedExtensions();
        if (acceptedExtensions) {
            const REGEXP_EXTENSION = new RegExp(`\.(${acceptedExtensions.join("|")})$`, "i");

            if (!REGEXP_EXTENSION.test(file.name)) {
                return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_EXTENSION;
            }
        }
        return null;
    };

    const validateSelectedFile = (file) => {
        if (!file) {
            return null;
        }

        const acceptedExtensions = getAcceptedExtensions();
        if (acceptedExtensions) {
            const REGEXP_EXTENSION = new RegExp(`\.(${acceptedExtensions.join("|")})$`, "i");

            if (!REGEXP_EXTENSION.test(file.name)) {
                return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_EXTENSION;
            }
        }

        if (file.url && !REGEXP_SCHEME.test(file.url)) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_URL_PATTERN;
        }

        if (file.size <= 0) {
            return CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").INVALID_FILE;
        }

        return null;
    };

    const onClickAddS3Url = async () => {
        const fileUrls = checkedFiles.map((file) => file.url);
        if (selectPending || !fileUrls?.length > 0) {
            return;
        }
        setSelectPending(true);

        let files = [];
        await Promise.allSettled(
            fileUrls.map(async (url) => {
                const count = createAsset.originFiles.length;
                const decodedUrl = decodeURIComponent(url).replace("+", " ");
                const name = decodedUrl.substring(decodedUrl.lastIndexOf("/") + 1, decodedUrl.length);
                const selectedFile = {
                    key: String(count),
                    displayName: `${createAsset.data?.name ?? ""}${name}`,
                    name: name,
                    url: decodedUrl,
                    locationType: UPLOAD_FILE_LOCATION_TYPE.S3,
                };

                const validationError = validateSelectedFile({ url, name: decodedUrl }, null);
                if (validationError !== null) {
                    files = files.concat([
                        {
                            ...selectedFile,
                            validationError,
                        },
                    ]);
                    return;
                }

                await getFilesInS3(stage.id, stage.endpoint, decodedUrl, project.id)
                    .then((response) => {
                        selectedFile.size = Number(response.headers["s3-object-content-length"]);
                        selectedFile.type = response.headers["s3-object-content-type"];

                        files = files.concat([selectedFile]);
                    })
                    .catch((e) => {
                        console.error(`error while trying to get content length`, e);
                        let error = CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").INVALID_FILE;
                        if (e.message.indexOf("400") > -1) {
                            error = CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").NOT_ALLOWED_URL_PATTERN;
                        } else if (e.message.indexOf("403") > -1) {
                            error = CONSTANTS("FILE_VALIDATE_ERROR_MESSAGE").UNAUTHORIZED;
                        }

                        files = files.concat([
                            {
                                ...selectedFile,
                                validationError: error,
                            },
                        ]);
                    });
            }),
        )
            .then(() => {
                onSubmit(files);
                setSelectPending(false);
                setCheckedFiles([]);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                onClose();
            });
    };

    const makeUploadBucketValue = (item, _sources) => {
        const folderData = _sources.hierarchy.folder.data;
        if (!folderData || !item) {
            return;
        }

        function findTree(list, target) {
            for (let item of list) {
                let foundTarget;
                if (item.children) {
                    foundTarget = findTree(item.children, target);
                    if (foundTarget) {
                        const parent = item.name;
                        return [parent].concat(foundTarget);
                    }
                }
                if (target.bucketName) {
                    if (item.name === target.bucketName) return [item.name];
                } else if (item.id === target.id) {
                    return [item.name];
                }
            }
        }

        const treeArray = findTree(folderData, item);

        return {
            bucket: treeArray && treeArray.splice(0, 1)[0],
            folderPath: treeArray && treeArray.join("/"),
        };
    };

    const onDropDisabled = (target, _sources) => {
        const bucketPath = makeUploadBucketValue(target, _sources);
        if (!bucketPath) return true;

        const buckets = _sources.hierarchy.folder.data;
        const currentBucket = buckets.filter((item) => item.name === bucketPath.bucket)[0];

        return currentBucket ? !currentBucket.uploadable : true;
    };

    const changeHierarchySelected = async (item, allowdFold = true, resources) => {
        const folderDataChangeIsSelected = (folderData, item) => {
            if (!folderData) return [];
            for (let folder of folderData) {
                folder.isSelected = false;
                if (folder.children.length > 0) {
                    folder.children = folderDataChangeIsSelected(folder.children, item);
                }
                if (folder.id === item.id) {
                    folder.isSelected = false;
                }
            }
            return folderData;
        };
        const hierarchy = resources.hierarchy;

        item.children = await getChildrenFolders(item);
        if (hierarchy.folder.selected && Object.keys(hierarchy.folder.selected).length) {
            hierarchy.folder.selected.isSelected = false;
        }
        if (item.hasOwnProperty("isFold") && allowdFold) {
            item.isFold = true;
        }

        folderDataChangeIsSelected(hierarchy.folder.data, item);
        item.isSelected = true;
        item.isEmpty = item.children.length > 0 ? false : true;

        return {
            item,
            hierarchy: {
                ...hierarchy,
                folder: {
                    ...hierarchy.folder,
                    selected: item,
                    data: hierarchy.folder.data,
                },
            },
        };
    };

    const onChangeSelectItem = (target) => {
        const url = getFileUrl(target);
        const _target = { ...target, url };

        if (isMultiFileSelect) {
            // 다중 선택 모드: 토글 방식
            if (checkedFiles.find((item) => item.url === target.url && item.lastModified === target.lastModified)) {
                const newSelectedItems = checkedFiles.filter(
                    (v) => !(v.url === target.url && v.lastModified === target.lastModified),
                );
                setCheckedFiles(newSelectedItems);
            } else {
                setCheckedFiles([...checkedFiles, _target]);
            }
        } else {
            // 단일 선택 모드: 마지막 클릭한 아이템만 선택
            setCheckedFiles([_target]);
        }
    };

    const getBucketName = (code) => {
        const bucket = getTargetBucket(code);
        return bucket && bucket.name;
    };

    const convertToFolders = (folders, item) => {
        return folders && folders.length
            ? folders.map((v) => {
                  if (item && item.children && item.children.length) {
                      const check = item.children.find((obj) => obj.name === v.path);

                      if (check) return check;
                  }
                  const _id = nanoid();
                  return {
                      id: `${_id}`,
                      code: item && item.code ? `${item.code}_${_id}` : `${_id}`,
                      children: [],
                      name: v.path,
                      prefix: `${item.prefix}${v.path}/`,
                      isEmpty: false,
                      isFold: false,
                      isSelected: false,
                      type: "folder",
                  };
              })
            : [];
    };

    const onCheckDownloadDisabled = (target, _sources) => {
        const bucketPath = makeUploadBucketValue(target, _sources);
        if (!bucketPath) return true;

        const buckdets = _sources.hierarchy.folder.data;
        const currentBucket = buckdets.filter((item) => item.name === bucketPath.bucket)[0];

        return currentBucket ? !currentBucket.downloadable : true;
    };

    const getFolders = async (bucket, key) => {
        try {
            const response = await listFolders(stage.endpoint, project.id, bucket, key);
            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const getChildrenFolders = async (item) => {
        if (!item) return [];

        const key = item.type !== "bucket" ? item.prefix : "";
        const bucketName = item.bucketName ?? getBucketName(item.code);
        if (!bucketName) return [];

        const response = await getFolders(bucketName, key);

        if (response.data && response.data.items && response.data.items.length > 0)
            return convertToFolders(response.data.items, item);
        else return [];
    };

    const getObjects = async (bucket, key, nextToken) => {
        try {
            if (objectsCancelTokenSource) {
                objectsCancelTokenSource.cancel();
            }
            const cancelTokenSource = axios._cancelToken.source();
            setObjectsCancelTokenSource(cancelTokenSource);

            const searchCondition = nextToken ? { nextToken } : sources.search;
            const response = await listObjectsV3(stage.endpoint, project.id, bucket, key, searchCondition);
            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const onHandleGetObjects = useCallback(
        async (item, nextToken, isChangedOffset) => {
            if (!item)
                return {
                    bucket: "",
                    key: "",
                    items: [],
                    nextToken: null,
                };

            const key = item.type !== "bucket" ? item.prefix : "";
            const bucketName = item.type === "bucket" ? item.name : item.bucketName ?? getBucketName(item.code);
            if (!bucketName) return { items: [], nextToken: null };
            const response = await getObjects(bucketName, key, nextToken);

            if (response && response.data) {
                const responseNextToken = response.data.nextToken;
                if (!isChangedOffset) {
                    setTokenPaginationInfo((prevTokenPaginationInfo) => ({
                        ...prevTokenPaginationInfo,
                        data: [
                            ...prevTokenPaginationInfo.data,
                            {
                                page: prevTokenPaginationInfo.data.length + 1,
                                nextToken: responseNextToken,
                            },
                        ],
                    }));
                }
                setTokenPaginationInfo((prevTokenPaginationInfo) => {
                    const pageCount = prevTokenPaginationInfo.data.length;
                    const offset = isChangedOffset
                        ? sources.search.offset
                        : (pageCount - 1) * sources.search.defaultViewCountByToken;
                    return {
                        offset,
                        data: prevTokenPaginationInfo.data,
                    };
                });

                const items = response.data?.items?.filter((item) => {
                    if (item.isFolder) return true;
                    else {
                        const decodedUrl = decodeURIComponent(item.path).replace("+", " ");
                        const validated = validateObject({ url: item.path, name: decodedUrl }, null);
                        return !validated;
                    }
                });

                return {
                    items: items || [],
                    nextToken: responseNextToken,
                };
            } else {
                setTokenPaginationInfo({
                    totalCount: 0,
                    offset: 0,
                    data: [],
                });
            }

            return {
                items: [],
                nextToken: null,
            };
        },
        [sources.search, state, getBucketName, getObjects],
    );

    const getTargetBucketByName = (name) => {
        if (sources && sources.hierarchy && sources.hierarchy.folder && sources.hierarchy.folder.data) {
            const targetBucket = sources.hierarchy.folder.data.find((v) => v.name === name);
            return targetBucket;
        }

        return null;
    };

    const getTargetBucket = useCallback(
        (code) => {
            const split = code && code.split("_");
            if (split?.length > 0) {
                const bucketItemCode = split[0];
                if (sources && sources.hierarchy && sources.hierarchy.folder && sources.hierarchy.folder.data) {
                    const targetBucket = sources.hierarchy.folder.data.find((v) => v.code === bucketItemCode);
                    return targetBucket;
                }
            }

            return null;
        },
        [sources],
    );

    const convertToNavView = async (data, item, resources) => {
        const bucket = item.bucketName ? getTargetBucketByName(item.bucketName) : getTargetBucket(item.code);
        let treeNavigation = [];

        function createFolderNav(code, data) {
            let navigation = [];
            let arrayCodes = code && code.split("_");
            let id = arrayCodes.splice(0, 1)[0];
            const target = data.find((item) => item.id === id) || [];
            let rest = [];
            if (arrayCodes.length > 0) {
                rest = createFolderNav(arrayCodes.join("_"), target.children || []);
            }
            navigation = navigation.concat(target, rest);
            return navigation;
        }

        function createFolderNavByName(name, data, changeHierarchySelected) {
            try {
                let navigation = [];
                let arrayNames = name && name.split("/");
                let targetName = arrayNames && arrayNames.splice(0, 1)[0];
                const target = data.find((folder) => folder.name === targetName) || [];
                let rest = [];
                if (name == "") return;
                if (arrayNames.length > 0) {
                    changeHierarchySelected(target, undefined, resources);
                    rest =
                        createFolderNavByName(arrayNames.join("/"), target.children || [], changeHierarchySelected) ||
                        [];
                }
                navigation = navigation.concat(target, rest);
                return navigation;
            } catch (e) {
                console.log(e);
            }
        }

        try {
            if (item.bucketName) {
                treeNavigation = createFolderNavByName(
                    `${item.bucketName}/${item.prefix}`,
                    data,
                    changeHierarchySelected,
                );
            } else {
                treeNavigation = createFolderNav(item.code, data);
                if (!treeNavigation.find((t) => t.id === item.id)) {
                    treeNavigation.push(item);
                }
            }
        } catch (e) {
            console.error("convertToNavView error : ", e);
            if (bucket) {
                treeNavigation.push(bucket);
            }
        }
        return treeNavigation.map((item, index) => {
            const _item = {
                ...item,
                isSelected: index === treeNavigation.length - 1 ? true : false,
                label: item.name,
                onClick: () => onClickTreePath(item),
            };
            return _item;
        });
    };

    const findTarget = (list, params) => {
        let find;
        const key = Object.keys(params)[0];
        const value = Object.values(params)[0];
        for (let item of list) {
            find = list.find((check) => check[key] === value);
            find = find ? find : findTarget(item.children, params);
            if (find) {
                break;
            }
        }
        return find;
    };

    const getObjectName = (path) => {
        const splitPath = path.split("/");
        const results = splitPath[splitPath.length - 1]?.split(".");

        return {
            name: results?.[0],
            extension: `.${results?.[1]}`,
        };
    };

    const onClickTreePath = async (item, blockUploadListClear, allowedFold, _sources) => {
        const resources = item.type === "bucket" ? _sources : sources;
        try {
            clearPolling();
            if (!item) {
                setState({ ...state, pending: false, selectedItems: [] });
                return;
            }
            if (blockUploadListClear) {
                dispatch(clear());
            }

            const bucketPath =
                item.type === "bucket" ? { bucket: item.name, folderPath: "" } : makeUploadBucketValue(item, resources);

            if (bucketPath) {
                bucketPath.uploadDisabled = onDropDisabled(item, resources);
                bucketPath.downloadDisabled = onCheckDownloadDisabled(item, resources);
            }

            //this.onClickReset();
            setState({
                ...state,
                pending: true,
                selectedItems: [],
                isChecked: false,
            });

            setTokenPaginationInfo({
                totalCount: 0,
                offset: 0,
                data: [],
            });

            await changeHierarchySelected(item, allowedFold, resources);
            const _objects = await onHandleGetObjects(item);
            const objects = _objects.items?.map((obj) => {
                const folderPath = bucketPath.folderPath ? `${bucketPath.folderPath}/` : "";
                const url = `s3://${bucketPath.bucket}/${folderPath}${obj.path}`;
                return { ...obj, url };
            });

            const folder = resources.hierarchy.folder;
            const historyIndex = (folder && folder.history && folder.history.index) || 0;
            const historyItems = (folder && folder.history && folder.history.items) || [];
            const navView = await convertToNavView(folder.data, item, resources);

            setSources({
                ...resources,
                isSearched: false,
                hierarchy: {
                    ...resources.hierarchy,
                    pending: false,
                    error: false,
                    folder: {
                        ...folder,
                        history: {
                            index:
                                historyItems?.[historyItems.length - 1]?.id === item.id
                                    ? historyIndex
                                    : historyIndex + 1,
                            items:
                                historyItems?.[historyItems.length - 1]?.id === item.id
                                    ? [...historyItems]
                                    : [...historyItems, item],
                            navView,
                        },
                        selected: item,
                    },
                },
                objects,
            });

            setState({ ...state, bucketPath, pending: false, selectedItems: [] });
        } catch (e) {
            console.log(e);
        }
    };

    const getCurrencPageToken = () => {
        const page =
            sources.search.offset === 0 ? 1 : sources.search.offset / sources.search.defaultViewCountByToken + 1;
        let prevPage;
        tokenPaginationInfo.data.map((v) => {
            if (v.page === page - 1) prevPage = v;
        });

        return prevPage ? prevPage.nextToken : "";
    };

    const changeHierarchySelected_v2 = async (item) => {
        const hierarchy = sources.hierarchy;
        item.children = await getChildrenFolders(item);
        item.isEmpty = item.children.length > 0 ? false : true;
        item.isSelected = true;
        if (hierarchy.folder.selected && Object.keys(hierarchy.folder.selected).length > 0) {
            hierarchy.folder.selected.isSelected = false;
        }
        // hierarchy.folder.history.navView = this.convertToNavView(hierarchy.folder.data, item);
        const newHierarchy = {
            ...hierarchy,
            folder: {
                ...hierarchy.folder,
                selected: item,
            },
        };
        return newHierarchy;
    };

    const pollingForUnSyncedFiles = async (item) => {
        // 요건 list에서 status가 SYNCED 가 아닐경우 해당 list들을 폴링해서 재갱신해주는 로직으로
        // 백엔드가 개선되면 불필요해질 로직임
        const doSomething = (item) => {
            return new Promise(
                (resolve) =>
                    (pollingTimeout = setTimeout(async () => {
                        try {
                            const key = item.type !== "bucket" ? item.prefix : "";
                            const bucketName = item.bucketName ?? getBucketName(item.code);
                            if (!bucketName) {
                                resolve("ERROR");
                                return;
                            }

                            resolve("SUCCEED");
                        } catch (error) {
                            resolve("ERROR");
                        }
                    }, 2000)),
            );
        };

        const loop = (item) => {
            return doSomething(item).then((results) => {
                if (results === "SUCCEED" || results === "ERROR") {
                    return results;
                } else {
                    if (pollingTimeout) {
                        return loop(item);
                    }
                    return "CANCELED";
                }
            });
        };

        return new Promise((resolve, reject) => {
            loop(item)
                .then((results) => {
                    resolve(results);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    const refreshFolderList = async (item, skipLoading, skipChangeHierarchy) => {
        const token = getCurrencPageToken();

        clearPolling();
        if (!skipLoading) {
            setState({ ...state, pending: true });
        }
        if (!skipChangeHierarchy) {
            const hierarchy = await changeHierarchySelected_v2(item);

            const objects = await onHandleGetObjects(item, token, true);

            const _sources = {
                ...sources,
                isSearched: false,
                hierarchy: {
                    ...hierarchy,
                    folder: {
                        ...hierarchy.folder,
                        history: {
                            ...hierarchy.folder.history,
                            navView: hierarchy.folder.history.navView.map((item, index) => {
                                item.isSelected = index === hierarchy.folder.history.navView.length - 1 ? true : false;
                                return item;
                            }),
                        },
                    },
                },
                objects: objects.items,
            };

            const bucketPath = makeUploadBucketValue(item, _sources);
            if (bucketPath) {
                bucketPath.uploadDisabled = onDropDisabled(item);
                bucketPath.downloadDisabled = onCheckDownloadDisabled(item);
            }
            setState({
                ...state,
                pending: false,
                selectedItems: [],
                isChecked: false,
                bucketPath,
            });
        } else {
            const objects = await onHandleGetObjects(item, token, true);
            setSources({
                ...sources,
                objects: objects.items,
            });

            setState({ ...state, pending: false, selectedItems: [], isChecked: false });
        }
    };

    const handleScroll = (e) => {
        const boxSize = stickyDomRef?.current?.getBoundingClientRect();
        const isSticky = boxSize && boxSize.top <= window.scrollY;

        setState({
            ...state,
            isControlSticky: isSticky,
        });
    };

    const getResourceBuckets = async () => {
        try {
            const params = { isDirectlyAccessible: true };
            const response = await listResourceBucketsV2(stage.endpoint, stage.id, project.id, params);

            const responseBuckets = response.data && response.data.results;

            const convertBuckets =
                responseBuckets &&
                responseBuckets.map((v, i) => {
                    const { id, name } = v;

                    return {
                        id,
                        code: id,
                        children: [],
                        name,
                        prefix: ``,
                        isEmpty: false,
                        isFold: false,
                        isSelected: i === 0,
                        type: "bucket",
                        bucketName: name,
                        uploadable: fp.getOr(false, "isUploadable", v),
                        downloadable: fp.getOr(false, "isDownloadable", v),
                    };
                });

            const _sources = {
                ...sources,
                hierarchy: {
                    ...sources.hierarchy,
                    pending: false,
                    error: false,
                    folder: {
                        ...sources.hierarchy.folder,
                        data: convertBuckets || {},
                        history: {
                            index: 0,
                            items: convertBuckets && convertBuckets.length > 0 ? [convertBuckets[0]] : [],
                            navView: convertBuckets && convertBuckets.length > 0 ? [convertBuckets[0]] : [],
                        },
                    },
                },
            };
            setSources(_sources);
            setSelectedBucket(convertBuckets[0]);
            return _sources;
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(async () => {
        window.addEventListener("scroll", handleScroll);

        dispatch(
            getStageSettings({
                stageId: stage.id,
                apiEndpoint: stage.endpoint,
            }),
        );

        if (match.path.indexOf("/cms") === 0) {
            setState({
                ...state,
                parentPath: "/cms",
                parentPathName: "CMS",
            });
        } else {
            setState({
                ...state,
                parentPath: "/contents",
                parentPathName: "Contents",
            });
        }

        const _sources = await getResourceBuckets();
        const defaultBucket = _sources?.hierarchy?.folder?.data?.[0];
        await onClickTreePath(defaultBucket, false, false, _sources);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const breadCrumbData = useMemo(() => {
        if (!hierarchy[hierarchyActive].history?.navView) return [];
        return hierarchy[hierarchyActive].history.navView.filter(
            (el, idx) => idx <= 1 || idx === hierarchy[hierarchyActive].history.navView.length - 1,
        );
    }, [hierarchy]);

    // useInView를 사용한 무한 스크롤
    useEffect(() => {
        if (inView && sources.hierarchy.folder?.selected) {
            const lastPageToken = tokenPaginationInfo.data[tokenPaginationInfo.data.length - 1]?.nextToken;
            if (lastPageToken) {
                const loadMore = async () => {
                    try {
                        const objects = await onHandleGetObjects(
                            sources.hierarchy.folder.selected,
                            lastPageToken,
                            false,
                        );
                        const items = objects.items?.map((obj) => ({ ...obj, url: getFilesInS3(obj) }));
                        if (items && items.length > 0) {
                            setSources((prevSources) => ({
                                ...prevSources,
                                objects: [...prevSources.objects, ...items],
                            }));
                        }
                    } catch (error) {
                        console.error("Error loading more objects:", error);
                    }
                };
                loadMore();
            }
        }
    }, [inView, sources.hierarchy.folder?.selected, tokenPaginationInfo.data.length, onHandleGetObjects]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            variant={"basic"}
            sx={{
                zIndex: 1002,
                ".MuiPaper-root": { minWidth: "768px", zIndex: 1002 },
                ".MuiBackdrop-root": { zIndex: 1001 },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        zIndex: 1001,
                    },
                },
            }}
        >
            <DialogTitle>
                <Grid container>
                    <Typography variant="h3">{t(`common::label::${"Select files"}`)}</Typography>
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`)}
                    onClickButton={onClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            <DialogContent sx={{ height: "100%", width: "768px", overflowY: "hidden", overflowX: "visible" }}>
                <Grid container flexDirection={"column"} gap={1.5} sx={{ height: "100%" }}>
                    <Grid item>
                        <Select
                            value={selectedBucket?.id || ""}
                            fullWidth
                            displayEmpty
                            MenuProps={{
                                disablePortal: true,
                                PaperProps: {
                                    sx: {
                                        zIndex: 1003,
                                    },
                                },
                                MenuListProps: {
                                    sx: {
                                        zIndex: 1003,
                                    },
                                },
                            }}
                            onChange={(e) => {
                                const bucketId = e.target.value;
                                const bucket = sources.hierarchy.folder?.data?.find((el) => el.id === bucketId);
                                if (bucket) {
                                    setSelectedBucket(bucket);
                                    onClickTreePath(bucket, null, null, sources);
                                }
                            }}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <IconStorage size={16} htmlColor={theme.palette.primary.dark} />
                                            <Typography variant="body2" color="text.secondary">
                                                {t(
                                                    "common::msg::Please select a repository.",
                                                    "Please select a repository.",
                                                )}
                                            </Typography>
                                        </Box>
                                    );
                                }
                                const bucket = sources.hierarchy.folder?.data?.find((el) => el.id === selected);
                                const bucketName = bucket?.name || "";
                                return (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <IconStorage size={16} htmlColor={theme.palette.primary.dark} />
                                        <Typography variant="body2">{bucketName}</Typography>
                                    </Box>
                                );
                            }}
                        >
                            {sources.hierarchy.folder?.data?.map((el) => {
                                return (
                                    <MenuItem key={el.id} value={el.id}>
                                        {el.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </Grid>
                    <Grid item sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Breadcrumbs separator={<IconSeparator size={8} />} sx={{ ml: 0.5 }}>
                            {hierarchy[hierarchyActive]?.history?.navView?.length
                                ? breadCrumbData.map((item, index) => (
                                      <Grid item key={item.id}>
                                          {index === 0 ? (
                                              <>
                                                  {item.indexingState === "FAILED" &&
                                                      item.indexingState === "PROCESSING" && (
                                                          <StorageSyncTooltip
                                                              indexingState={item.indexingState}
                                                              indexingRequestedAt={item.indexingRequestedAt}
                                                              indexingCompletedAt={item.indexingCompletedAt}
                                                          />
                                                      )}
                                                  <Button
                                                      variant={"text"}
                                                      size={"small"}
                                                      color={"greySecondary"}
                                                      onClick={(e) => {
                                                          onClickTreePath(item, null, null, sources);
                                                      }}
                                                      className={`depth ${item.isSelected ? "current" : ""}`}
                                                      startIcon={
                                                          <>
                                                              {hierarchy[hierarchyActive].history.navView.length > 1 ? (
                                                                  <ImageBucketS3Off width={16} height={16} />
                                                              ) : (
                                                                  <ImageBucketS3On width={16} height={16} />
                                                              )}
                                                              {item.type === "bucket" && (
                                                                  <Grid
                                                                      container
                                                                      flexDirection={"column"}
                                                                      justifyContent={"center"}
                                                                      gap={0.25}
                                                                      sx={{
                                                                          ".MzcCpIcon-root + * ": {
                                                                              margin: 0,
                                                                          },
                                                                      }}
                                                                  >
                                                                      {item.uploadable ? (
                                                                          <IconArrowUpload
                                                                              size={6}
                                                                              htmlColor={"#2763ea"}
                                                                          />
                                                                      ) : (
                                                                          <IconArrowUpload
                                                                              size={6}
                                                                              color={"disabled"}
                                                                          />
                                                                      )}
                                                                      {item.downloadable ? (
                                                                          <IconArrowDownload
                                                                              size={6}
                                                                              htmlColor={"#2763ea"}
                                                                          />
                                                                      ) : (
                                                                          <IconArrowDownload
                                                                              size={6}
                                                                              color={"disabled"}
                                                                          />
                                                                      )}
                                                                  </Grid>
                                                              )}
                                                          </>
                                                      }
                                                  >
                                                      {item.name}
                                                  </Button>
                                              </>
                                          ) : (
                                              <React.Fragment>
                                                  {hierarchy[hierarchyActive].history.navView.length > 2 &&
                                                      index === 1 && (
                                                          <Menu
                                                              width={160}
                                                              placement={"right"}
                                                              type={"icon"}
                                                              label={<IconMoreHoriz size={16} />}
                                                              color={"inherit"}
                                                              size={"small"}
                                                              list={hierarchy[hierarchyActive].history.navView.slice(
                                                                  1,
                                                                  hierarchy[hierarchyActive].history.navView.length - 1,
                                                              )}
                                                          />
                                                      )}

                                                  {(breadCrumbData.length === 2 ||
                                                      (breadCrumbData.length > 2 &&
                                                          index === breadCrumbData.length - 1)) && (
                                                      <>
                                                          <Button
                                                              className={`depth ${
                                                                  hierarchy[hierarchyActive]?.history?.navView[
                                                                      hierarchy[hierarchyActive]?.history?.navView
                                                                          .length - 1
                                                                  ].isSelected
                                                                      ? "current"
                                                                      : ""
                                                              }`}
                                                              variant="text"
                                                              size="small"
                                                              color="secondary"
                                                              startIcon={<ImageFolderSmallOn width={16} height={16} />}
                                                          >
                                                              <EllipsisTooltip
                                                                  text={
                                                                      hierarchy[hierarchyActive]?.history?.navView[
                                                                          hierarchy[hierarchyActive]?.history?.navView
                                                                              .length - 1
                                                                      ].name
                                                                  }
                                                                  variant={"body1_400"}
                                                                  maxWidth={250}
                                                              />
                                                          </Button>
                                                      </>
                                                  )}
                                              </React.Fragment>
                                          )}
                                      </Grid>
                                  ))
                                : ""}
                        </Breadcrumbs>
                        <Grid item sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant={"body3_500"}>
                                {t("common::label::Selected files", "Selected files")}
                                <Typography color={"primary"}>{` (${checkedFiles.length})`}</Typography>
                            </Typography>
                            <Divider
                                orientation={"vertical"}
                                flexItem
                                sx={{
                                    ml: 1.5,
                                    mr: 1,
                                    height: "16px",
                                    alignSelf: "center",
                                }}
                            />
                            <Button
                                size={"small"}
                                variant={"text"}
                                color="greySecondary"
                                startIcon={<IconResetSmall size={16} />}
                                onClick={() => setCheckedFiles([])}
                            >
                                <Typography variant={"body3_400"}>{t("common::label::Reset")}</Typography>
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: `flex`,
                            flexDirection: `column`,
                            position: "relative",
                            overflowY: "auto",
                            // DAM-646: 스크롤바 생길 때 콘텐츠 밀림 방지
                            scrollbarGutter: "stable",
                            maxHeight: "580px",
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                            }}
                        >
                            <TableContainer
                                sx={{
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    minHeight: "411px",
                                    flex: 1,
                                }}
                            >
                                <Table
                                    stickyHeader
                                    sx={{
                                        width: "max-content",
                                        minWidth: "100%",
                                        "& .MuiTableBody-root": {
                                            height: "100%",
                                        },
                                        "& .MuiTableBody-root .MuiTableRow-root": {
                                            height: "32px",
                                        },
                                        "& .MuiTableBody-root .MuiTableCell-root": {
                                            height: "32px",
                                            padding: "4px 12px",
                                            whiteSpace: "nowrap",
                                        },
                                    }}
                                >
                                    <colgroup>
                                        <col style={{ width: "auto" }} />
                                        <col style={{ width: "130px", minWidth: "130px" }} />
                                    </colgroup>
                                    <TableHead sx={{ display: "none" }}>
                                        <TableRow>
                                            <TableCell sx={{ width: "auto" }}>
                                                {t(`common::label::Name`, `Name`)}
                                            </TableCell>
                                            <TableCell sx={{ width: "130px", minWidth: "130px" }}>
                                                {t(`common::label::Size`, `Size`)}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.pending && (
                                            <TableRow
                                                sx={{
                                                    height: `calc("100%" - 12px)`,
                                                }}
                                            >
                                                <TableCell
                                                    colSpan={2}
                                                    sx={{
                                                        height: "100%",
                                                        textAlign: "center",
                                                        verticalAlign: "middle",
                                                        padding: 0,
                                                        borderBottom: "none",
                                                    }}
                                                >
                                                    <Grid
                                                        container
                                                        flexDirection={"column"}
                                                        sx={{
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            minHeight: "402px",
                                                        }}
                                                    >
                                                        <div className={"loading-block"}>
                                                            <span className={"loading-content"}></span>
                                                        </div>
                                                    </Grid>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {!state.pending && sources.objects && sources.objects.length === 0 && (
                                            <TableRow
                                                sx={{
                                                    height: "100%",
                                                    "& .MuiTableCell-root": {
                                                        height: "100%",
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    colSpan={2}
                                                    sx={{
                                                        height: "100%",
                                                        textAlign: "center",
                                                        verticalAlign: "middle",
                                                        borderBottom: "none",
                                                    }}
                                                >
                                                    <Grid
                                                        container
                                                        flexDirection={"column"}
                                                        gap={2}
                                                        sx={{
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            height: "100%",
                                                            minHeight: "402px",
                                                        }}
                                                    >
                                                        <ImageEmpty width={80} height={72} />
                                                        <Typography>
                                                            {t(`common::msg::There is no file.`, "There is no file.")}
                                                        </Typography>
                                                    </Grid>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {!state.pending && sources.objects && sources.objects.length > 0 && (
                                            <React.Fragment>
                                                {folderList &&
                                                    folderList.map((v, i) => {
                                                        return (
                                                            <TableRow key={i}>
                                                                <TableCell sx={{ width: "auto" }}>
                                                                    <Grid
                                                                        container
                                                                        alignItems={"center"}
                                                                        flexWrap={"nowrap"}
                                                                    >
                                                                        <Grid item>
                                                                            <Grid
                                                                                container
                                                                                alignItems={"center"}
                                                                                gap={0.5}
                                                                            >
                                                                                {v.isFolder && (
                                                                                    <ObjectIcon
                                                                                        path={v.path}
                                                                                        isFolder={v.isFolder}
                                                                                    />
                                                                                )}
                                                                                <Grid item xs>
                                                                                    {v.isFolder &&
                                                                                    !(
                                                                                        v.status &&
                                                                                        v.status !== "SYNCED"
                                                                                    ) ? (
                                                                                        <Typography
                                                                                            variant={"body1_500"}
                                                                                        >
                                                                                            <Button
                                                                                                size={"small"}
                                                                                                variant={"solid"}
                                                                                                sx={{
                                                                                                    minWidth: 0,
                                                                                                    textAlign: `left`,
                                                                                                }}
                                                                                                onClick={(e) => {
                                                                                                    const foundBucketChildren =
                                                                                                        sources.hierarchy.folder.data.filter(
                                                                                                            (item) =>
                                                                                                                item.name ===
                                                                                                                state
                                                                                                                    .bucketPath
                                                                                                                    ?.bucket,
                                                                                                        );

                                                                                                    const targetFolder =
                                                                                                        findTarget(
                                                                                                            foundBucketChildren,
                                                                                                            {
                                                                                                                prefix: `${
                                                                                                                    state
                                                                                                                        .bucketPath
                                                                                                                        ?.folderPath
                                                                                                                        ? `${state.bucketPath?.folderPath}/`
                                                                                                                        : ""
                                                                                                                }${
                                                                                                                    v.path
                                                                                                                }/`,
                                                                                                            },
                                                                                                        );
                                                                                                    if (!targetFolder)
                                                                                                        return;
                                                                                                    onClickTreePath(
                                                                                                        targetFolder,
                                                                                                    );
                                                                                                }}
                                                                                            >
                                                                                                {v.path.split("/")[0]}
                                                                                            </Button>
                                                                                        </Typography>
                                                                                    ) : (
                                                                                        <Stack direction={"column"}>
                                                                                            <EllipsisTypography
                                                                                                variant={"body1_500"}
                                                                                                noWrap
                                                                                            >
                                                                                                {
                                                                                                    getObjectName(
                                                                                                        v.path,
                                                                                                    ).name
                                                                                                }
                                                                                            </EllipsisTypography>
                                                                                            <Typography
                                                                                                variant={"body3_400"}
                                                                                            >
                                                                                                {
                                                                                                    getObjectName(
                                                                                                        v.path,
                                                                                                    ).extension
                                                                                                }
                                                                                            </Typography>
                                                                                        </Stack>
                                                                                    )}
                                                                                </Grid>
                                                                                {v.status && v.status !== "SYNCED" ? (
                                                                                    <CircularProgress
                                                                                        size={12}
                                                                                        color={"greySecondary"}
                                                                                    />
                                                                                ) : (
                                                                                    ""
                                                                                )}
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                </TableCell>
                                                                <TableCell sx={{ width: "130px", minWidth: "130px" }}>
                                                                    {v.isFolder
                                                                        ? "-"
                                                                        : v.size
                                                                        ? convertToByte(v.size)
                                                                        : ""}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                {fileList && (
                                                    <>
                                                        {fileList.map((v, i) => {
                                                            return (
                                                                <TableRow
                                                                    key={i}
                                                                    className={`${
                                                                        v.status && v.status !== "SYNCED"
                                                                            ? "is-loading"
                                                                            : ""
                                                                    }`}
                                                                    sx={{
                                                                        backgroundColor: checkedFiles.some(
                                                                            (file) => file.url === v.url,
                                                                        )
                                                                            ? alpha(
                                                                                  theme.palette.greySecondary.A100,
                                                                                  0.24,
                                                                              )
                                                                            : undefined,
                                                                    }}
                                                                    onClick={() => onChangeSelectItem(v)}
                                                                >
                                                                    <TableCell
                                                                        sx={{
                                                                            width: "auto",
                                                                        }}
                                                                    >
                                                                        <Grid
                                                                            container
                                                                            alignItems={"center"}
                                                                            flexWrap={"nowrap"}
                                                                        >
                                                                            {isMultiFileSelect && (
                                                                                <Grid item>
                                                                                    <Checkbox
                                                                                        color={"greySecondary"}
                                                                                        checked={checkedFiles.some(
                                                                                            (file) =>
                                                                                                file.url === v.url,
                                                                                        )}
                                                                                        onChange={() =>
                                                                                            onChangeSelectItem(v)
                                                                                        }
                                                                                        disabled={
                                                                                            v.status &&
                                                                                            v.status !== "SYNCED"
                                                                                        }
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                            )}

                                                                            <Grid item>
                                                                                <Grid
                                                                                    container
                                                                                    alignItems={"center"}
                                                                                    gap={1}
                                                                                    ml={1}
                                                                                >
                                                                                    {v.isFolder && (
                                                                                        <ObjectIcon
                                                                                            path={v.path}
                                                                                            isFolder={v.isFolder}
                                                                                        />
                                                                                    )}
                                                                                    <Grid item xs>
                                                                                        {v.isFolder ? (
                                                                                            <Typography
                                                                                                variant={"body1_500"}
                                                                                            >
                                                                                                <Button
                                                                                                    variant={"solid"}
                                                                                                    sx={{
                                                                                                        minWidth: 0,
                                                                                                        textAlign: `left`,
                                                                                                    }}
                                                                                                    onClick={(e) => {
                                                                                                        const foundBucketChildren =
                                                                                                            sources.hierarchy.folder.data.filter(
                                                                                                                (
                                                                                                                    item,
                                                                                                                ) =>
                                                                                                                    item.name ===
                                                                                                                    state
                                                                                                                        .bucketPath
                                                                                                                        .bucket,
                                                                                                            );
                                                                                                        const targetFolder =
                                                                                                            findTarget(
                                                                                                                foundBucketChildren,
                                                                                                                {
                                                                                                                    prefix: `${
                                                                                                                        state
                                                                                                                            .bucketPath
                                                                                                                            ?.folderPath
                                                                                                                            ? `${state.bucketPath?.folderPath}/`
                                                                                                                            : ""
                                                                                                                    }${
                                                                                                                        v.path
                                                                                                                    }/`,
                                                                                                                },
                                                                                                            );
                                                                                                        if (
                                                                                                            !targetFolder
                                                                                                        )
                                                                                                            return;
                                                                                                        onClickTreePath(
                                                                                                            targetFolder,
                                                                                                        );
                                                                                                    }}
                                                                                                >
                                                                                                    {v.path}
                                                                                                </Button>
                                                                                            </Typography>
                                                                                        ) : (
                                                                                            <Stack direction={"column"}>
                                                                                                <EllipsisTooltip
                                                                                                    text={
                                                                                                        getObjectName(
                                                                                                            v.path,
                                                                                                        ).name
                                                                                                    }
                                                                                                    variant={
                                                                                                        "body1_500"
                                                                                                    }
                                                                                                    maxWidth={512}
                                                                                                />
                                                                                                <Typography
                                                                                                    variant={
                                                                                                        "body3_400"
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        getObjectName(
                                                                                                            v.path,
                                                                                                        ).extension
                                                                                                    }
                                                                                                </Typography>
                                                                                            </Stack>
                                                                                        )}
                                                                                    </Grid>
                                                                                    {v.status &&
                                                                                    v.status !== "SYNCED" ? (
                                                                                        <CircularProgress
                                                                                            size={12}
                                                                                            color={"secondary"}
                                                                                        />
                                                                                    ) : (
                                                                                        ""
                                                                                    )}
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{ width: "130px", minWidth: "130px" }}
                                                                    >
                                                                        {v.isFolder
                                                                            ? "-"
                                                                            : v.size
                                                                            ? convertToByte(v.size)
                                                                            : ""}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                        {tokenPaginationInfo.data[tokenPaginationInfo.data.length - 1]
                                                            ?.nextToken && (
                                                            <TableRow>
                                                                <TableCell
                                                                    colSpan={2}
                                                                    sx={{ textAlign: "center", py: 2 }}
                                                                >
                                                                    <div ref={loadMoreRef} id={"loadMoreDiv"} />
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider orientation={"horizontal"} sx={{ mt: 2 }} />
            <DialogActions>
                <Button
                    color="secondary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    variant="text"
                >
                    {t(`common::label::${"Cancel"}`)}
                </Button>
                <Button
                    color="primary"
                    onClick={onClickAddS3Url}
                    disabled={selectPending || checkedFiles.length === 0}
                    loading={selectPending}
                    variant="contained"
                >
                    {t(`common::label::${"Select"}`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogSelectS3FilesV2;
